import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Col, Row } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  updateLocalSettingsMasterConfigToggle,
  updateLocalSettingsMasterConfigToggleSuccess,
} from "../../../store/generalSettings/action";
import { getProductList } from "../../../store/product/action";
import {
  SHIFT_CHARGEABLE_CONFIG_ID,
  SHIFT_CHARGEABLE_CONFIG_KEY,
  SHIFT_CHARGEABLE_NO,
  SHIFT_CHARGEABLE_YES,
  SHIFT_FEE_PRODUCT_CONFIG_ID,
  SHIFT_FEE_PRODUCT_CONFIG_KEY,
  SHIFT_FEE_PRODUCT_NONE,
  getDefaultShiftFeeProductId,
  isShiftChargeableByDefault,
  patchShiftDefaults,
} from "../../../helpers/functions/shiftDefaults";

// Task 24857 - where a new shift starts, set once for the whole company.
//
// Both settings are config items that had no screen of their own: the only way
// to change them was to type a word - or worse, a raw product id - into the
// generic Settings > Config Items grid. "Choose the product" is not something
// anybody can do from a text box they have to know an id for, so the product is
// a picker off the live product list and the tick is the same On/Off pair the
// feedback tool uses.
//
// The two sit in one panel because they are one decision: naming a fee product
// is itself a statement that shifts get billed, so the tick follows the product
// and the standalone tick only matters for a company that wants new shifts
// flagged chargeable without committing to a single product.
export default function ShiftDefaultsPanel() {
  const dispatch = useDispatch();

  const [chargeable, setChargeable] = useState(() =>
    isShiftChargeableByDefault()
  );
  const [productId, setProductId] = useState(() =>
    getDefaultShiftFeeProductId()
  );

  // Put a control back where it was if the write is rejected. Only ever holds
  // the most recent save, which is all that can be in flight from one panel.
  const revertRef = useRef(null);

  const { productList, saveResponse } = useSelector((state) => ({
    productList: state.ProductCrmIT?.productList?.product_list || [],
    saveResponse:
      state.GeneralSettings.updateLocalSettingsMasterConfigToggleSuccess,
  }));

  useEffect(() => {
    if (!productList || productList.length === 0) {
      dispatch(getProductList());
    }
    // Mount only - the catalogue is fetched once, and re-running on
    // productList would re-fetch forever for a company with no products.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One save path for both rows. The value written is always a non-empty word
  // or a number: configcompany/append refuses an empty value and the resolver
  // reads '0' as unset, so "off" and "no product" each need a written-down
  // value of their own.
  const save = (configId, configKey, value, revert) => {
    dispatch(
      updateLocalSettingsMasterConfigToggle([
        {
          MasterConfigItem_Id: configId,
          // Neither of these is a personal preference - how a company bills its
          // shifts is a company decision - so no per-staff override.
          ConfigCompany_FlagAllowUserOverride: "0",
          ConfigCompany_Value: value,
        },
      ])
    );
    // Patch localStorage now so the very next "New shift" uses it rather than
    // waiting for a re-login; get_configitem_data is only rebuilt at login.
    patchShiftDefaults({ [configKey]: value });
    revertRef.current = revert;
  };

  const handleChargeable = (next) => {
    if (next === chargeable) return;
    const previous = chargeable;
    setChargeable(next);
    save(
      SHIFT_CHARGEABLE_CONFIG_ID,
      SHIFT_CHARGEABLE_CONFIG_KEY,
      next ? SHIFT_CHARGEABLE_YES : SHIFT_CHARGEABLE_NO,
      () => setChargeable(previous)
    );
  };

  const handleProduct = (next) => {
    if (String(next) === String(productId)) return;
    const previous = productId;
    setProductId(next);
    save(
      SHIFT_FEE_PRODUCT_CONFIG_ID,
      SHIFT_FEE_PRODUCT_CONFIG_KEY,
      next === "" ? SHIFT_FEE_PRODUCT_NONE : String(next),
      () => setProductId(previous)
    );
  };

  useEffect(() => {
    if (isEmpty(saveResponse)) return;
    if (saveResponse.status === 200) {
      toast.success("Shift defaults saved");
    } else {
      toast.error(saveResponse.message);
      if (revertRef.current) revertRef.current();
      // Undo the optimistic localStorage patch by writing back what the two
      // settings actually resolved to before this attempt.
      patchShiftDefaults({
        [SHIFT_CHARGEABLE_CONFIG_KEY]: chargeable
          ? SHIFT_CHARGEABLE_YES
          : SHIFT_CHARGEABLE_NO,
        [SHIFT_FEE_PRODUCT_CONFIG_KEY]:
          productId === "" ? SHIFT_FEE_PRODUCT_NONE : String(productId),
      });
    }
    revertRef.current = null;
    dispatch(updateLocalSettingsMasterConfigToggleSuccess([]));
    // Keyed on the response alone. chargeable/productId are read for the
    // rollback but must not re-trigger it - they change on every click, and a
    // re-run here would re-toast the previous save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveResponse]);

  const selectedProduct = (productList || []).find(
    (product) => String(product.Id) === String(productId)
  );
  // A named product forces the tick on, so show it on rather than leaving a
  // stale "Off" highlighted next to a product that will tick it anyway.
  const hasDefaultProduct = String(productId) !== "";
  const tickShownOn = chargeable || hasDefaultProduct;

  return (
    <Card className="mb-0">
      <CardBody>
        <h6 className="mb-1">
          <i className="ri-price-tag-3-line text-primary me-2" />
          New shift - billing defaults
        </h6>
        <p className="text-muted fs-13">
          Where the &quot;Charge a placement / finders fee on completion&quot;
          section starts on a <strong>new</strong> shift. Shifts already saved
          are never changed by this.
        </p>

        <Row className="g-3 align-items-start">
          <Col md={6}>
            <label className="form-label mb-1" htmlFor="shiftDefaultFeeProduct">
              Default fee product
            </label>
            <select
              id="shiftDefaultFeeProduct"
              className="form-select"
              value={productId}
              onChange={(e) => handleProduct(e.target.value)}
            >
              <option value="">No default - choose it on each shift</option>
              {(productList || []).map((product) => (
                <option key={product.Id} value={String(product.Id)}>
                  {product.Name}
                  {product.AmountLocalSubTotal !== undefined &&
                  product.AmountLocalSubTotal !== null
                    ? ` - ${Number(product.AmountLocalSubTotal).toFixed(2)}`
                    : ""}
                </option>
              ))}
            </select>
            <div className="text-muted fs-13 mt-1">
              {hasDefaultProduct
                ? "Every new shift arrives with this product chosen and the chargeable box already ticked. The product is what carries the VAT rate and the nominal on the invoice line."
                : "With no default, the fee product has to be picked by hand on every chargeable shift."}
            </div>
            {hasDefaultProduct && !selectedProduct ? (
              <div className="text-warning fs-13 mt-1">
                <i className="ri-error-warning-line align-bottom me-1" />
                The saved product is not in the current product list - new
                shifts will fall back to no default until this is set again.
              </div>
            ) : null}
          </Col>

          <Col md={6}>
            <label className="form-label mb-1">
              Tick &quot;chargeable&quot; on new shifts
            </label>
            <div>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${
                    tickShownOn ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => handleChargeable(true)}
                  disabled={hasDefaultProduct}
                >
                  On
                </button>
                <button
                  type="button"
                  className={`btn ${
                    !tickShownOn ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => handleChargeable(false)}
                  disabled={hasDefaultProduct}
                >
                  Off
                </button>
              </div>
            </div>
            <div className="text-muted fs-13 mt-1">
              {hasDefaultProduct
                ? "Held on while a default fee product is set - a company that has named the product it bills is billing its shifts. Clear the product above to control this on its own."
                : "On its own this only flags new shifts as chargeable and leaves the product to be chosen on each one."}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
